/**
 * Card detail → Rewards (§11, §13.3 "caps" tab).
 *
 * Top to bottom, the questions in the order the user asks them:
 *   1. What does this card actually pay — and has that changed lately?
 *   2. How much cap is left right now? (only if the user entered capped rules)
 *   3. What rules have I entered, and when did each version apply?
 *   4. Did the bank actually pay what my rules predicted? (§11.4)
 *
 * Rules are optional (user's ruling, 2026-11-03: terms change too often to keep
 * typed in). The learned rate is the default, and the change alarm compares a
 * card against its own past, so nothing here asks for input.
 *
 * Every figure is an estimate and is marked as one (§2.7).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, History, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { HeadroomBar, trimRate } from "@/components/caps/HeadroomBar";
import { RuleFormDialog, type RuleFormMode } from "@/components/caps/RuleFormDialog";
import { Badge, Button, ErrorState, Panel, ProvisionalChip } from "@/components/ui/primitives";
import {
  capsApi,
  type CapRule,
  type CardRewards,
  type Category,
  type EstimateVsActual,
  type Statement,
} from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";

const HISTORY_WINDOW = 6;

export function RewardsTab({
  cardId,
  statements,
}: {
  cardId: string;
  statements: Statement[] | undefined;
}) {
  const rewards = useQuery({
    queryKey: ["caps", "card", cardId],
    queryFn: () => capsApi.card(cardId),
  });
  const rules = useQuery({
    queryKey: ["caps", "rules", cardId],
    queryFn: () => capsApi.rules(cardId),
  });
  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: capsApi.categories,
    staleTime: 5 * 60_000,
  });

  const [editing, setEditing] = useState<{ mode: RuleFormMode; rule?: CapRule } | null>(null);

  // A new rule defaults to "in force from" the start of the history MyDeck
  // compares against, so estimate-vs-actual has something to say on day one.
  const suggestedFrom = useMemo(() => {
    if (!statements?.length) return null;
    const recent = statements.slice(0, HISTORY_WINDOW);
    const oldest = recent[recent.length - 1];
    return oldest ? (oldest.period_start ?? oldest.statement_date) : null;
  }, [statements]);

  if (rewards.isError) {
    return (
      <Panel>
        <ErrorState message={(rewards.error as Error).message} onRetry={() => rewards.refetch()} />
      </Panel>
    );
  }
  if (!rewards.data || !rules.data) {
    return (
      <Panel className="space-y-2 p-5">
        <div className="skeleton h-9 w-full" />
        <div className="skeleton h-9 w-full" />
      </Panel>
    );
  }

  const r = rewards.data;

  return (
    <div className="space-y-6">
      <BasisPanel rewards={r} onAdd={() => setEditing({ mode: "add" })} />

      {/* The rules alarm when rules exist; otherwise the card-against-its-past
          one. Never both: they would say the same thing twice. */}
      {(r.stale_warning ?? r.learned.drop_warning) && (
        <div
          role="status"
          className="flex gap-3 rounded-lg border border-warn/30 bg-warn/5 p-4 text-sm text-ink"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" aria-hidden />
          <p>{r.stale_warning ?? r.learned.drop_warning}</p>
        </div>
      )}

      {(r.in_force.length > 0 || r.milestones.length > 0) && <NowPanel rewards={r} />}

      {/* Rules are optional: with none, the "Add a rule (optional)" button above
          is the whole invitation — no empty panel asking for terms. */}
      {rules.data.length > 0 && (
        <RulesPanel
          rules={rules.data}
          categories={categories}
          onAdd={() => setEditing({ mode: "add" })}
          onEdit={(rule, mode) => setEditing({ rule, mode })}
        />
      )}

      {r.history.length > 0 && <HistoryPanel rewards={r} />}

      {editing && (
        <RuleFormDialog
          cardId={cardId}
          mode={editing.mode}
          rule={editing.rule}
          suggestedFrom={suggestedFrom}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// --- 1. how this card is planned ----------------------------------------------

function BasisPanel({ rewards: r, onAdd }: { rewards: CardRewards; onAdd: () => void }) {
  const l = r.learned;
  return (
    <Panel className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="label-micro">The planner uses</div>
          <div className="font-display text-lg font-semibold text-ink">
            {r.basis === "rules"
              ? "Your rules for this card"
              : r.basis === "learned"
                ? "What this card has actually paid"
                : "Nothing yet"}
          </div>
          <p className="max-w-xl text-sm text-muted">
            {r.basis === "rules"
              ? "Caps and category bonuses are tracked from the terms you entered."
              : r.basis === "learned"
                ? "Measured from your statements, so it follows the bank's changes by itself. It's one blended rate: it can't see caps or category bonuses."
                : "No cashback on this card's statements yet. A rate appears once one pays out."}
          </p>
        </div>
        {r.basis !== "rules" && (
          // Optional, so not the loud button: rules only earn their keep for a
          // card whose cap the user wants to watch.
          <Button
            variant="default"
            size="sm"
            onClick={onAdd}
            title="Optional — only useful if you want to watch a cap"
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add a rule (optional)
          </Button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-hairline pt-4">
        <Sparkles className="h-4 w-4 self-center text-faint" aria-hidden />
        <span className="label-micro">Learned rate</span>
        <span className="font-display text-xl font-semibold tabular-nums text-ink">
          {l.rate_percent !== null ? `${trimRate(l.rate_percent)}%` : "—"}
        </span>
        {l.confidence === "weak" && <Badge tone="warn">Rough</Badge>}
        <p className="w-full text-xs text-faint">{l.basis}</p>
        {l.cycles.length > 1 && <CycleTrend cycles={l.cycles} />}
        {r.rewards_note && l.rate_percent !== null && Number(l.rate_percent) === 0 && (
          <p className="w-full text-xs text-muted">Your note: {r.rewards_note}</p>
        )}
      </div>
    </Panel>
  );
}

/** Each recent statement's rate, oldest first — the trend behind the average. */
function CycleTrend({ cycles }: { cycles: CardRewards["learned"]["cycles"] }) {
  return (
    <ol className="mt-2 flex w-full flex-wrap gap-1.5" aria-label="Rate by statement, oldest first">
      {cycles.map((c) => (
        <li
          key={c.statement_id}
          className="rounded-md border border-hairline px-2 py-1 text-xs tabular-nums"
          title={`${formatMoney(c.cashback)} on ${formatMoney(c.spend)} spent`}
        >
          <span className="text-faint">{formatDate(c.statement_date)}</span>{" "}
          <span className="text-ink">
            {c.rate_percent !== null ? `${trimRate(c.rate_percent)}%` : "no spend"}
          </span>
        </li>
      ))}
    </ol>
  );
}

// --- 2. right now -----------------------------------------------------------

function NowPanel({ rewards: r }: { rewards: CardRewards }) {
  const anyUnverified = r.in_force.some((s) => s.channel_unverified);
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="font-display text-base font-medium text-ink">Right now</h2>
        <ProvisionalChip kind="est" />
      </div>
      <Panel className="space-y-5 p-5">
        {r.in_force.map((s) => (
          <div key={s.rule_id} className="space-y-1.5">
            <HeadroomBar status={s} />
            {!s.unavailable && (
              <p className="text-xs text-faint">
                {formatMoney(s.matched_spend)} spent on this rule
                {s.period_start && s.period_end && (
                  <>
                    {" "}
                    · {formatDate(s.period_start)} – {formatDate(s.period_end)}
                    {s.days_left !== null && ` · ${s.days_left}d left`}
                  </>
                )}
                {s.provisional_count > 0 &&
                  ` · ${s.provisional_count} from bank alerts, not yet on a statement`}
              </p>
            )}
            {s.uncategorised_spend && Number(s.uncategorised_spend) > 0 && (
              <p className="text-xs text-warn">
                {formatMoney(s.uncategorised_spend)} of purchases this period have no category, so
                MyDeck can't tell if they count. The cap may have less room than shown.
              </p>
            )}
          </div>
        ))}

        {r.milestones.map((m) => (
          <div key={m.rule_id} className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="text-ink">{m.name}</span>
              <span className="tabular-nums text-muted">
                {m.unavailable ? "—" : m.reached ? "Reached" : `${formatMoney(m.remaining)} to go`}
              </span>
            </div>
            {m.unavailable ? (
              <p className="text-xs text-faint">{m.unavailable}</p>
            ) : (
              <>
                <div className="h-1.5 w-full overflow-hidden rounded-full border border-dashed border-hairline-strong">
                  <div
                    className={cn("h-full rounded-full", m.reached ? "bg-paid/70" : "bg-brass/70")}
                    style={{
                      width: `${Math.min((Number(m.spend) / Math.max(Number(m.target), 1)) * 100, 100)}%`,
                    }}
                  />
                </div>
                <p className="text-xs text-faint">
                  {formatMoney(m.spend)} of {formatMoney(m.target)}
                  {m.reward_note && ` for ${m.reward_note}`}
                  {m.days_left !== null && ` · ${m.days_left}d left`}
                </p>
              </>
            )}
          </div>
        ))}

        {anyUnverified && <p className="text-xs text-faint">{r.channel_caveat}</p>}
      </Panel>
    </section>
  );
}

// --- 3. the rules -----------------------------------------------------------

function describe(rule: CapRule, categories: Category[] | undefined): string {
  const name = (id: string) => categories?.find((c) => c.id === id)?.name ?? "a category";
  const c = rule.conditions ?? {};
  const parts: string[] = [];
  if (c.channel === "online") parts.push("online");
  if (c.channel === "offline") parts.push("in shops");
  if (c.category_ids?.length) parts.push(c.category_ids.map(name).join(", "));
  if (c.merchant_pattern) parts.push(`merchants ${c.merchant_pattern}`);
  if (c.min_txn_amount) parts.push(`purchases over ${formatMoney(c.min_txn_amount)}`);
  let text = parts.length ? `On ${parts.join(" · ")}` : "On all purchases";
  if (c.exclude_category_ids?.length)
    text += `, except ${c.exclude_category_ids.map(name).join(", ")}`;
  if (c.exclude_merchant_pattern) text += `, except ${c.exclude_merchant_pattern}`;
  return text;
}

function RulesPanel({
  rules,
  categories,
  onAdd,
  onEdit,
}: {
  rules: CapRule[];
  categories: Category[] | undefined;
  onAdd: () => void;
  onEdit: (rule: CapRule, mode: RuleFormMode) => void;
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) => capsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["caps"] });
      qc.invalidateQueries({ queryKey: ["plan"] });
      toast.success("Rule deleted.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Versions of one rule share its name; newest first within a name.
  const groups = useMemo(() => {
    const byName = new Map<string, CapRule[]>();
    for (const rule of rules) {
      byName.set(rule.name, [...(byName.get(rule.name) ?? []), rule]);
    }
    return [...byName.entries()].map(([name, versions]) => ({
      name,
      versions: versions.sort((a, b) => b.valid_from.localeCompare(a.valid_from)),
    }));
  }, [rules]);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-base font-medium text-ink">Your rules</h2>
        <Button variant="default" size="sm" onClick={onAdd}>
          <Plus className="h-4 w-4" aria-hidden />
          Add a rule
        </Button>
      </div>
      {/* Only rendered with at least one rule (see the caller). */}
      <Panel className="divide-y divide-hairline">
        {groups.map(({ name, versions }) => (
          <div key={name} className="space-y-2 p-4">
            {versions.map((rule, i) => {
              const current = i === 0;
              return (
                <div
                  key={rule.id}
                  className={cn(
                    "flex flex-wrap items-start justify-between gap-3",
                    !current && "opacity-60",
                  )}
                >
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={cn("text-sm", current ? "font-medium text-ink" : "text-muted")}
                      >
                        {current ? name : "Earlier version"}
                      </span>
                      {rule.rule_type === "rate_cap" ? (
                        <Badge tone="brass">
                          {trimRate(rule.rate_percent ?? "0")}%
                          {rule.cap_amount ? ` · max ${formatMoney(rule.cap_amount)}` : " · no cap"}
                        </Badge>
                      ) : (
                        <Badge tone="neutral">
                          Milestone {formatMoney(rule.milestone_target_spend)}
                        </Badge>
                      )}
                      <span className="text-xs text-faint">
                        per {rule.period === "calendar_month" ? "month" : "statement cycle"}
                      </span>
                    </div>
                    <p className="text-xs text-muted">{describe(rule, categories)}</p>
                    <p className="text-xs text-faint">
                      From {formatDate(rule.valid_from)}
                      {rule.valid_to ? ` to ${formatDate(rule.valid_to)}` : " · still in force"}
                      {rule.notes && ` · ${rule.notes}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {current && !rule.valid_to && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(rule, "revise")}
                        title="The bank changed this rule from a date"
                      >
                        <History className="h-3.5 w-3.5" aria-hidden />
                        Bank changed it
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(rule, "fix")}
                      aria-label={`Fix ${rule.name}`}
                      title="Fix a mistake in this rule"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${rule.name}`}
                      title="Delete this rule"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete “${rule.name}”? This can't be undone.`)) {
                          remove.mutate(rule.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </Panel>
    </section>
  );
}

// --- 4. estimate vs actual --------------------------------------------------

const VERDICT: Record<
  EstimateVsActual["verdict"],
  { label: string; tone: "paid" | "warn" | "neutral" | "brass" }
> = {
  match: { label: "Matches", tone: "paid" },
  under: { label: "Paid less", tone: "warn" },
  over: { label: "Paid more", tone: "brass" },
  pending: { label: "Waiting", tone: "neutral" },
};

function HistoryPanel({ rewards: r }: { rewards: CardRewards }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-display text-base font-medium text-ink">
          Did the bank pay what the rules say?
        </h2>
        <p className="text-xs text-faint">
          Your rules applied to each statement's purchases, next to the cashback the bank actually
          credited. Two short cycles in a row usually means the terms changed.
        </p>
      </div>
      <Panel className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-sm">
            <thead>
              <tr className="border-b border-hairline text-left">
                <th className="label-micro px-5 py-2 font-normal">Statement</th>
                <th className="label-micro px-3 py-2 text-right font-normal">Rules say</th>
                <th className="label-micro px-3 py-2 text-right font-normal">Bank paid</th>
                <th className="label-micro px-5 py-2 font-normal">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {[...r.history].reverse().map((h) => (
                <tr key={h.statement_id} title={h.note || undefined}>
                  <td className="whitespace-nowrap px-5 py-2.5 tabular-nums text-ink">
                    {formatDate(h.statement_date)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-muted">
                    {formatMoney(h.estimated)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-ink">
                    {formatMoney(h.actual)}
                  </td>
                  <td className="px-5 py-2.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge tone={VERDICT[h.verdict].tone}>{VERDICT[h.verdict].label}</Badge>
                      {h.approximate && (
                        <span className="text-xs text-faint" title={r.channel_caveat}>
                          approx.
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </section>
  );
}
